import { useI18n } from "../../../i18n";
import { Goal } from "./ScenePanels";
import { reactionOf } from "../lib/play";
import { FRIENDS_GOAL, GATE_ASKS, PAGES_GOAL, SHIPS_GOAL, BLINKS_PER_SHIP } from "../lib/little";
import type { Gate, Greet, Harbor, Story } from "../lib/little";
import {
  BOARD_LEN, BRICKS_GOAL, GOALS_GOAL, LEVEL_HOLD_MS, MAZE, MAZE_N, QUIZ_KEY, QUIZ_PASS, REACTION_GOAL, REACTION_MS, RPS_ROUNDS,
  allFaces, fastStarts, goalsDone, goalsIn, goalsMatch, meet,
} from "../lib/games";
import type { Ball, Dice, Goals, Hand, Level, Quiz, Reaction, Rps } from "../lib/games";
import { GALLERY_HOLD_MS, MARQUEE_REPEATS, nameOf, readWords, repeatsOf } from "../lib/scroll";
import type { Gallery, Scroll } from "../lib/scroll";

/**
 * 차시 장면 둘째 묶음의 판(PICNIC-MB-FIELD-001 v0.2 §4 · LE01~05 · L01 · L02 · L04 · L07~L12 · L14) — 상태는 lib(순수), 여기는 그리기만.
 * 목표 줄은 `data-done` 으로 시험이 읽는다(`fs-mb-scene-goal`). 화면 문구는 DR-01 — 점수 · 승패 낱말 없이 무엇이 일어났는지만.
 */

const HAND: Record<Hand, string> = { rock: "✊", paper: "✋", scissors: "✌️" };
const sec = (ms: number) => (ms / 1000).toFixed(1);

/** 켜짐 비트열 → 작은 LED 판 */
function Mini({ bits, cols = 5 }: { bits: string; cols?: number }) {
  return (
    <span className="pf-mb__mini" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }} aria-hidden="true">
      {bits.split("").map((b, i) => <i key={i} data-on={b === "1" || undefined} />)}
    </span>
  );
}
/** 세로 줄들 → 가로로 긴 LED 띠(전광판 · 명찰) */
function Strip({ cols }: { cols: readonly string[] }) {
  const bits = [0, 1, 2, 3, 4].map((y) => cols.map((c) => c[y]).join("")).join("");
  return <Mini bits={bits} cols={Math.max(1, cols.length)} />;
}

// ── 저학년 ─────────────────────────────────────────────────────────

export function GreetPanel({ s, icon }: { s: Greet; icon: string | null }) {
  const { t } = useI18n();
  const kids = ["🧒", "👧", "🧑"];
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-greet">
      <div className="pf-mb__row" aria-hidden="true">
        {kids.map((k, i) => <span key={i} className="pf-mb__friend" data-on={i < s.friends || undefined}>{k}{i < s.friends ? `👋${reactionOf(icon)}` : ""}</span>)}
      </div>
      <p className="pf-mb__scene-state">{t("student.microbit.scene.greet.state", { n: Math.min(s.friends, FRIENDS_GOAL), goal: FRIENDS_GOAL })}</p>
      <Goal id="fs-mb-scene-goal" done={s.friends >= FRIENDS_GOAL} text={t("student.microbit.scene.greet.goal", { goal: FRIENDS_GOAL })} />
    </div>
  );
}

export function StoryPanel({ s }: { s: Story }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-story">
      <div className="pf-mb__row">
        {Array.from({ length: PAGES_GOAL }, (_, i) => (
          <span key={i} className="pf-mb__page" data-on={i < s.pages.length || undefined}>
            <b>{t(`student.microbit.scene.story.page.${i}`)}</b>
            <span aria-hidden="true">{s.pages[i] ? reactionOf(s.pages[i]!) : "📖"}</span>
          </span>
        ))}
      </div>
      <p className="pf-mb__scene-state">{t("student.microbit.scene.story.state", { n: Math.min(s.pages.length, PAGES_GOAL), goal: PAGES_GOAL })}</p>
      <Goal id="fs-mb-scene-goal" done={s.pages.length >= PAGES_GOAL} text={t("student.microbit.scene.story.goal", { goal: PAGES_GOAL })} />
    </div>
  );
}

export function HarborPanel({ s, lit }: { s: Harbor; lit: boolean }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-harbor">
      <div className="pf-mb__row" aria-hidden="true">
        <span className="pf-mb__big" data-on={lit || undefined}>🗼{lit ? "💡" : ""}</span>
        <span className="pf-mb__big">{"⛵".repeat(s.ships) || "🌊"}</span>
      </div>
      <p className="pf-mb__scene-state">
        {s.interval !== null ? t("student.microbit.scene.harbor.blink", { s: sec(s.interval) }) : t("student.microbit.scene.harbor.wait")}
        {" · "}{t("student.microbit.scene.harbor.next", { n: s.fast, per: BLINKS_PER_SHIP })}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.ships >= SHIPS_GOAL} text={t("student.microbit.scene.harbor.goal", { goal: SHIPS_GOAL })} />
    </div>
  );
}

export function GatePanel({ s }: { s: Gate }) {
  const { t } = useI18n();
  const done = s.i >= GATE_ASKS.length;
  const ask = GATE_ASKS[Math.min(s.i, GATE_ASKS.length - 1)]!;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-gate">
      <div className="pf-mb__row" aria-hidden="true">
        <span className="pf-mb__big">{done ? "🚪✨" : "💂"}</span>
        <span className="pf-mb__bubble" data-testid="fs-mb-gate-ask">{done ? t("student.microbit.scene.gate.open") : t(`student.microbit.scene.gate.ask.${ask}`)}</span>
      </div>
      <p className="pf-mb__scene-state">
        {t("student.microbit.scene.gate.state", { n: s.i, goal: GATE_ASKS.length })}
        {!done && s.other ? ` · ${t(`student.microbit.scene.gate.other.${ask}`)}` : ""}
      </p>
      <Goal id="fs-mb-scene-goal" done={done} text={t("student.microbit.scene.gate.goal", { goal: GATE_ASKS.length })} />
    </div>
  );
}

export function BoardGamePanel({ s }: { s: Dice }) {
  const { t } = useI18n();
  const last = s.rolls[s.rolls.length - 1];
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-boardGame">
      <div className="pf-mb__lane" style={{ gridTemplateColumns: `repeat(${BOARD_LEN + 1}, 1fr)` }}>
        {Array.from({ length: BOARD_LEN + 1 }, (_, i) => (
          <span key={i} className="pf-mb__lane-cell" data-me={s.pos === i || undefined}>{s.pos === i ? (s.pos >= BOARD_LEN ? "🎉" : "🐰") : i === BOARD_LEN ? "🏁" : ""}</span>
        ))}
      </div>
      <p className="pf-mb__scene-state">{last ? t("student.microbit.scene.boardGame.rolled", { n: last }) : t("student.microbit.scene.boardGame.wait")} · {s.pos}/{BOARD_LEN}</p>
      <Goal id="fs-mb-scene-goal" done={s.pos >= BOARD_LEN} text={t("student.microbit.scene.boardGame.goal")} />
    </div>
  );
}

// ── 화면 읽기 — 글자 · 그림 ─────────────────────────────────────────

export function NameTagPanel({ s, icon }: { s: Scroll; icon: string | null }) {
  const { t } = useI18n();
  const name = nameOf(s);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-nameTag">
      <div className="pf-mb__tag">
        <span className="pf-mb__tag-name" data-testid="fs-mb-name">
          {name ? (name.text ?? <Strip cols={name.cols} />) : t("student.microbit.scene.nameTag.noName")}
        </span>
        <span className="pf-mb__big" aria-hidden="true">{icon ? reactionOf(icon) : "❔"}</span>
        <span className="pf-mb__big" aria-hidden="true">🧒</span>
      </div>
      <Goal id="fs-mb-scene-goal" done={!!name && !!icon} text={t("student.microbit.scene.nameTag.goal")} />
    </div>
  );
}

export function GalleryPanel({ s }: { s: Gallery }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-gallery">
      <div className="pf-mb__row">
        {s.pieces.map((p) => <span key={p} className="pf-mb__frame"><Mini bits={p} /></span>)}
        {s.pieces.length === 0 ? <span className="pf-mb__frame pf-mb__frame--empty">🖼️</span> : null}
      </div>
      <p className="pf-mb__scene-state">
        {s.cand && !s.pieces.includes(s.cand) ? t("student.microbit.scene.gallery.holding", { s: sec(Math.max(0, GALLERY_HOLD_MS - s.held)) }) : t("student.microbit.scene.gallery.state", { n: s.pieces.length })}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.pieces.length >= 1} text={t("student.microbit.scene.gallery.goal")} />
    </div>
  );
}

export function MarqueePanel({ s }: { s: Scroll }) {
  const { t } = useI18n();
  const r = repeatsOf(s);
  const words = readWords(s).slice(-4).join(" ");
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-marquee">
      <div className="pf-mb__marquee"><Strip cols={s.cols.length ? s.cols.slice(-30) : ["00000"]} /></div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-marquee-words">{words || t("student.microbit.scene.marquee.wait")}</p>
      {r.word ? <p className="pf-mb__scene-state">{t("student.microbit.scene.marquee.repeats", { word: r.word, n: r.n })}</p> : null}
      <Goal id="fs-mb-scene-goal" done={r.n >= MARQUEE_REPEATS} text={t("student.microbit.scene.marquee.goal")} />
    </div>
  );
}

// ── 게임 ───────────────────────────────────────────────────────────

export function GoalsPanel({ s }: { s: Goals }) {
  const { t } = useI18n();
  const last = s.shots[s.shots.length - 1];
  const n = goalsIn(s);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-goals">
      <div className="pf-mb__row" aria-hidden="true">
        <span className="pf-mb__big">{last === undefined ? "⚽" : last ? "⚽🥅" : "⚽❌"}</span>
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-goals-shot">
        {last === undefined ? t("student.microbit.scene.goals.wait") : last ? t("student.microbit.scene.goals.in") : t("student.microbit.scene.goals.miss")}
      </p>
      <p className="pf-mb__scene-state" data-testid="fs-mb-goals-count">
        {t("student.microbit.scene.goals.count", { n, board: s.board ?? "—" })} {goalsMatch(s) ? "✓" : ""}
      </p>
      <Goal id="fs-mb-scene-goal" done={goalsDone(s)} text={t("student.microbit.scene.goals.goal", { goal: GOALS_GOAL })} />
    </div>
  );
}

export function RpsPanel({ s }: { s: Rps }) {
  const { t } = useI18n();
  const r = s.rounds[s.rounds.length - 1];
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-rps">
      <div className="pf-mb__row" aria-hidden="true">
        <span className="pf-mb__big">🧒 {r ? HAND[r.me] : "❔"}</span>
        <span className="pf-mb__big">{r ? HAND[r.friend] : "❔"} 🐻</span>
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-rps-meet">
        {r ? (meet(r) === "same" ? t("student.microbit.scene.rps.same") : t(`student.microbit.scene.rps.over.${meet(r) === "me" ? r.me : r.friend}`)) : t("student.microbit.scene.rps.wait")}
      </p>
      <p className="pf-mb__scene-state">{t("student.microbit.scene.rps.rounds", { n: s.rounds.length, goal: RPS_ROUNDS })}</p>
      <Goal id="fs-mb-scene-goal" done={s.rounds.length >= RPS_ROUNDS} text={t("student.microbit.scene.rps.goal", { goal: RPS_ROUNDS })} />
    </div>
  );
}

export function ReactionPanel({ s, lit }: { s: Reaction; lit: boolean }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-reaction">
      <div className="pf-mb__row" aria-hidden="true">
        <span className="pf-mb__big">{lit ? "🟢" : "🔴"}</span>
        <span className="pf-mb__big">🏃</span>
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-reaction-times">
        {s.times.length ? s.times.slice(-5).map((ms) => `${sec(ms)}${ms <= REACTION_MS ? "✓" : ""}`).join(" · ") : t("student.microbit.scene.reaction.wait")}
      </p>
      {s.early > 0 ? <p className="pf-mb__scene-state">{t("student.microbit.scene.reaction.early", { n: s.early })}</p> : null}
      <Goal id="fs-mb-scene-goal" done={fastStarts(s) >= REACTION_GOAL} text={t("student.microbit.scene.reaction.goal", { s: sec(REACTION_MS), goal: REACTION_GOAL, n: Math.min(fastStarts(s), REACTION_GOAL) })} />
    </div>
  );
}

export function QuizPanel({ s }: { s: Quiz }) {
  const { t } = useI18n();
  const q = Math.min(s.q, QUIZ_KEY.length - 1);
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-quiz">
      <p className="pf-mb__bubble" data-testid="fs-mb-quiz-q">
        {s.passed ? t("student.microbit.scene.quiz.done") : `${t("student.microbit.scene.quiz.no", { n: q + 1, total: QUIZ_KEY.length })} ${t(`student.microbit.scene.quiz.q.${q}`)}`}
      </p>
      {s.last ? (
        <p className="pf-mb__scene-state" data-testid="fs-mb-quiz-last">
          {s.last.ok ? t("student.microbit.scene.quiz.right") : t(`student.microbit.scene.quiz.key.${QUIZ_KEY[s.last.q] ? "o" : "x"}`)}
        </p>
      ) : null}
      <p className="pf-mb__scene-state">{t("student.microbit.scene.quiz.state", { n: s.right })}{s.again > 0 && !s.passed && s.q === 0 ? ` · ${t("student.microbit.scene.quiz.again")}` : ""}</p>
      <Goal id="fs-mb-scene-goal" done={s.passed} text={t("student.microbit.scene.quiz.goal", { total: QUIZ_KEY.length, pass: QUIZ_PASS })} />
    </div>
  );
}

export function DicePanel({ s }: { s: Dice }) {
  const { t } = useI18n();
  const faces = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];
  const last = s.rolls[s.rolls.length - 1];
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-dice">
      <div className="pf-mb__row">
        {faces.map((f, i) => <span key={i} className="pf-mb__die" data-on={s.seen.includes(i + 1) || undefined}>{f}</span>)}
      </div>
      <p className="pf-mb__scene-state">{last ? t("student.microbit.scene.dice.rolled", { n: last }) : t("student.microbit.scene.dice.wait")} · {t("student.microbit.scene.dice.seen", { n: s.seen.length })}</p>
      <Goal id="fs-mb-scene-goal" done={allFaces(s)} text={t("student.microbit.scene.dice.goal")} />
    </div>
  );
}

export function BallMazePanel({ s }: { s: Ball }) {
  const { t } = useI18n();
  const pct = (v: number) => `${((v + 0.5) / MAZE_N) * 100}%`;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-ballMaze">
      <div className="pf-mb__maze" style={{ gridTemplateColumns: `repeat(${MAZE_N}, 1fr)` }}>
        {MAZE.join("").split("").map((c, i) => <span key={i} data-wall={c === "#" || undefined}>{c === "H" ? "🕳️" : ""}</span>)}
        <span className="pf-mb__ball" style={{ left: pct(s.x), top: pct(s.y) }} aria-hidden="true">{s.done ? "🎉" : "⚪"}</span>
      </div>
      <Goal id="fs-mb-scene-goal" done={s.done} text={t("student.microbit.scene.ballMaze.goal")} />
    </div>
  );
}

export function LevelPanel({ s, tiltX, check }: { s: Level; tiltX: number; check: boolean }) {
  const { t } = useI18n();
  const deg = Math.max(-20, Math.min(20, tiltX / 30));
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-level">
      <div className="pf-mb__shelf" style={{ transform: `rotate(${deg}deg)` }} aria-hidden="true">
        <span>{"🧱".repeat(s.bricks)}</span>
      </div>
      <div className="pf-mb__bar" role="progressbar" aria-valuemin={0} aria-valuemax={LEVEL_HOLD_MS} aria-valuenow={s.held}>
        <span className="pf-mb__bar-fill" style={{ width: `${(s.held / LEVEL_HOLD_MS) * 100}%` }} />
      </div>
      <p className="pf-mb__scene-state">
        {check ? (s.needRelease ? t("student.microbit.scene.level.release") : t("student.microbit.scene.level.holding")) : t("student.microbit.scene.level.wait")}
        {" · "}{t("student.microbit.scene.level.bricks", { n: Math.min(s.bricks, BRICKS_GOAL), goal: BRICKS_GOAL })}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.bricks >= BRICKS_GOAL} text={t("student.microbit.scene.level.goal", { goal: BRICKS_GOAL })} />
    </div>
  );
}
